/**
 * Painel largo da sessão de estudo. Só apresenta o que a view já leu:
 * contagens, acerto, sequência e a fatia seguinte. Não busca, não lê o
 * hook da sessão e não reconta cartões.
 */
import { Pencil } from "lucide-react";
import {
  acertoIndisponivel,
  proporcaoDeContagens,
  rotuloEdicaoRapida,
  sequenciaIndisponivel,
  textoAcerto,
  textoPosicaoTotal,
  textoRecordeSequencia,
  textoSequencia,
  textoVariacaoAcerto,
  type SessaoPainelLateralProps,
} from "./painelLateral";
import { ListaAtalhosSessao } from "./SessaoControles";

const SECAO_CLASS = "flex flex-col gap-3 rounded-card border border-edge bg-surface p-4";
const TITULO_CLASS = "text-xs font-semibold tracking-wide text-ink-muted";
const CHIP_CLASS =
  "rounded-chip bg-surface-muted px-2 py-0.5 text-xs text-ink-secondary";

function classeMetrica(indisponivel: boolean): string {
  const cor = indisponivel ? "text-ink-muted" : "text-ink";
  return `text-xl font-semibold tabular-nums ${cor}`;
}

export function SessaoPainelLateral(props: SessaoPainelLateralProps): JSX.Element {
  const {
    posicao,
    total,
    contagens,
    acerto,
    sequencia,
    acertoDelta,
    sequenciaRecorde,
    proximos,
    atalhosExtra,
    onEditarRapido,
  } = props;
  const acertoAusente = acertoIndisponivel(acerto);
  const sequenciaAusente = sequenciaIndisponivel(sequencia);
  const proporcao = proporcaoDeContagens(contagens);
  const variacaoAcertoTexto = textoVariacaoAcerto(acertoDelta);
  const recordeSequenciaTexto = textoRecordeSequencia(sequenciaRecorde);

  return (
    <aside
      data-sessao-painel=""
      aria-label="Painel da sessão"
      className="flex w-full flex-col gap-4"
    >
      <section className={SECAO_CLASS}>
        <h2 className={TITULO_CLASS}>Sessão de hoje</h2>
        <p
          data-sessao-posicao-total=""
          className="text-3xl font-semibold tabular-nums text-ink"
        >
          {textoPosicaoTotal(posicao, total)}
        </p>
        <div
          role="img"
          aria-label={`Novos ${contagens.new}, Aprendendo ${contagens.learning}, Revisar ${contagens.review}`}
          className="flex h-2 w-full gap-0.5 overflow-hidden rounded-full bg-surface-muted"
        >
          {proporcao.new > 0 && (
            <div className="bg-accent-purple-ink" style={{ width: `${proporcao.new}%` }} />
          )}
          {proporcao.learning > 0 && (
            <div className="bg-accent-orange-ink" style={{ width: `${proporcao.learning}%` }} />
          )}
          {proporcao.review > 0 && (
            <div className="bg-accent-green-ink" style={{ width: `${proporcao.review}%` }} />
          )}
        </div>
        <ul className="m-0 flex list-none flex-col gap-2 p-0">
          <li className="flex items-center justify-between gap-3">
            <span className="flex items-center gap-2 text-sm text-ink-secondary">
              <span aria-hidden className="h-2 w-2 shrink-0 rounded-full bg-accent-purple-ink" />
              Novos
            </span>
            <span data-sessao-novos="" className={`${CHIP_CLASS} tabular-nums`}>
              {contagens.new}
            </span>
          </li>
          <li className="flex items-center justify-between gap-3">
            <span className="flex items-center gap-2 text-sm text-ink-secondary">
              <span aria-hidden className="h-2 w-2 shrink-0 rounded-full bg-accent-orange-ink" />
              Aprendendo
            </span>
            <span data-sessao-aprendendo="" className={`${CHIP_CLASS} tabular-nums`}>
              {contagens.learning}
            </span>
          </li>
          <li className="flex items-center justify-between gap-3">
            <span className="flex items-center gap-2 text-sm text-ink-secondary">
              <span aria-hidden className="h-2 w-2 shrink-0 rounded-full bg-accent-green-ink" />
              Revisar
            </span>
            <span data-sessao-revisar="" className={`${CHIP_CLASS} tabular-nums`}>
              {contagens.review}
            </span>
          </li>
        </ul>
      </section>

      <section className={SECAO_CLASS}>
        <h2 className={TITULO_CLASS}>Desempenho</h2>
        <div className="grid grid-cols-2 gap-3">
          <div className="flex flex-col gap-1">
            <span className="text-xs text-ink-muted">Acerto</span>
            <span
              data-sessao-acerto=""
              className={classeMetrica(acertoAusente)}
              aria-label={acertoAusente ? "indisponível" : undefined}
            >
              {textoAcerto(acerto)}
            </span>
            {variacaoAcertoTexto !== null && (
              <span data-sessao-acerto-variacao="" className="text-xs text-ink-secondary">
                {variacaoAcertoTexto}
              </span>
            )}
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-xs text-ink-muted">Sequência</span>
            <span
              data-sessao-sequencia=""
              className={classeMetrica(sequenciaAusente)}
              aria-label={sequenciaAusente ? "indisponível" : undefined}
            >
              {textoSequencia(sequencia)}
            </span>
            {recordeSequenciaTexto !== null && (
              <span data-sessao-sequencia-recorde="" className="text-xs text-ink-secondary">
                {recordeSequenciaTexto}
              </span>
            )}
          </div>
        </div>
      </section>

      <ListaAtalhosSessao atalhosExtra={atalhosExtra} />

      <section className={SECAO_CLASS}>
        {onEditarRapido ? (
          <div className="flex items-center gap-2">
            <h2 className={TITULO_CLASS}>Próximos</h2>
            <span
              data-proximos-selo-admin=""
              className="rounded-chip border border-admin/[0.35] bg-admin/[0.15] px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-admin-ink"
            >
              Admin
            </span>
          </div>
        ) : (
          <h2 className={TITULO_CLASS}>Próximos</h2>
        )}
        {proximos.length === 0 ? (
          <p className="m-0 text-sm text-ink-secondary">Nenhum cartão depois deste.</p>
        ) : (
          <ul className="m-0 max-h-64 list-none overflow-y-auto p-0">
            {proximos.map((item) => (
              <li
                key={item.id}
                data-proximo-cartao={item.id}
                className="flex items-center justify-between gap-3 py-2"
              >
                <span
                  data-proximo-texto=""
                  className="min-w-0 flex-1 truncate text-sm text-ink"
                  title={item.frente}
                >
                  {item.frente}
                </span>
                {onEditarRapido ? (
                  <span className="flex shrink-0 items-center gap-2">
                    <span data-proximo-tipo="" className={CHIP_CLASS}>
                      {item.tipo}
                    </span>
                    <button
                      type="button"
                      data-proximo-editar={item.id}
                      aria-label={rotuloEdicaoRapida(item)}
                      onClick={() => onEditarRapido(item.id)}
                      className="inline-flex shrink-0 items-center justify-center rounded-full border border-admin/[0.35] bg-admin/[0.15] p-1.5 text-admin outline-none hover:bg-admin/[0.22] focus-visible:ring-2 focus-visible:ring-admin/[0.42]"
                    >
                      <Pencil size={14} aria-hidden />
                    </button>
                  </span>
                ) : (
                  <span data-proximo-tipo="" className={`${CHIP_CLASS} shrink-0`}>
                    {item.tipo}
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>
    </aside>
  );
}
